import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Sheet } from 'react-modal-sheet';
import { t } from 'i18next';
import { Button, ThumbnailPhotoCard } from '@peacecover/component';
import { LikeFill, LikeLine, ReportLine, Xmark } from '@peacecover/icon';
import usePhotoCardQuery from '../../../components/api/queries/usePhotoCardQuery';
import updateCollectedPhotoCard from '../../../components/api/mutations/legacy/updateCollectedPhotoCard';
import updateWishedPhotoCard from '../../../components/api/mutations/legacy/updateWishedPhotoCard';
import { setEvent } from '@pocapan/shared/utils/ga';
import { TagsList } from '../../../components/photocard/TagsList';
import ChangeCollectedCountPopup from './ChangeCollectedCountPopup';
import type { PhotoCardType } from '../../types/PhotoCardType';

interface PhotoCardDetailBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  photoCardId: string;
}

const PhotoCardDetailBottomSheet = ({
  isOpen,
  onClose,
  photoCardId
}: PhotoCardDetailBottomSheetProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isShowChangeCollectCountPopup, setIsShowChangeCollectCountPopup] = useState(false);
  const photoCard = usePhotoCardQuery(photoCardId);

  const navigatePhotoCardReport = (photoCardId: string) => {
    navigate(`/report/photo-card?photoCardId=${photoCardId}`);
  };

  const invalidateQueries = () => {
    queryClient.invalidateQueries(['photoCardV1', photoCard.photoCardId]);
    queryClient.invalidateQueries(['photoCardEvents', photoCard.artistId, photoCard.memberId]);
  };

  const updateCollectedCount = async (card: PhotoCardType, collectedCount: number) => {
    setEvent({
      category: 'PhotoCard',
      action: collectedCount > 0 ? 'Collected PhotoCard' : 'UnCollected PhotoCard'
    });
    const response = await updateCollectedPhotoCard(card.photoCardId, collectedCount);
    if (response.success) {
      invalidateQueries();
    }
  };

  const toggleWishedState = async (isWished: boolean) => {
    setEvent({
      category: 'PhotoCard',
      action: isWished ? 'UnWished PhotoCard' : 'Wished PhotoCard'
    });
    const response = await updateWishedPhotoCard(isWished, photoCard.artistId, photoCard.memberId, [
      photoCard.photoCardId
    ]);
    if (response.success) {
      invalidateQueries();
    }
  };

  if (!photoCard) {
    return null;
  }

  return (
    <>
      <Sheet isOpen={isOpen} onClose={onClose} detent="content">
        <Sheet.Container className="px-[24px] !rounded-t-[20px] pt-[24px] !pb-[calc(10px+env(safe-area-inset-bottom))] !bg-background-elevated">
          <Sheet.Header className="flex justify-between items-center">
            <Xmark className="text-text-main-lv1" />
            <div className="flex items-center gap-[12px]">
              <ReportLine
                className="text-text-main-lv1"
                onClick={() => navigatePhotoCardReport(photoCardId)}
              />
              <button type="button" onClick={() => toggleWishedState(photoCard.isWished)}>
                {photoCard?.isWished ? (
                  <LikeFill className="text-on-warning" />
                ) : (
                  <LikeLine className="text-text-main-lv1" />
                )}
              </button>
            </div>
          </Sheet.Header>
          <Sheet.Content className="mt-[24px]">
            <div className="flex items-center gap-[17px]">
              <div className="flex-none">
                <ThumbnailPhotoCard
                  imageUrl={photoCard?.largeThumbnailUrl || photoCard?.pictureUrl}
                  className="w-[75px] h-[118px] rounded-[8px]"
                />
              </div>
              <div className="flex flex-col gap-[9px] items-start">
                <p className="text-[18px] font-semibold text-text-main-lv2">
                  {t('photoCardDetailPage.title', {
                    memberName: photoCard.memberName,
                    categoryName: photoCard.categoryName,
                    categoryEmoji: photoCard.categoryEmoji
                  })}
                </p>
                <p className="text-[14px] font-medium text-text-main-lv3 leading-[17px]">
                  {photoCard.eventName}
                </p>
                <div className="flex flex-row gap-[6px] flex-wrap">
                  <TagsList photoCard={photoCard} />
                </div>
              </div>
            </div>
            <div className="flex gap-[10px] mt-[14px]">
              <Button
                btnColor={photoCard.collectedCount > 0 ? 'secondary' : 'primary'}
                className="rounded-[8px] h-[45px] w-full text-[14px] font-semibold flex items-center justify-center"
                onClick={() => setIsShowChangeCollectCountPopup(true)}
              >
                Collected
                {photoCard.collectedCount > 0 && (
                  <div className="h-[13px] text-[9px] font-semibold rounded-full bg-primary text-on-primary leading-[11px] ml-[3px] px-[4px] flex items-center justify-center">
                    {photoCard.collectedCount}
                  </div>
                )}
              </Button>
              <Button
                btnColor="warning"
                disabled={photoCard.collectedCount === 0}
                onClick={() => updateCollectedCount(photoCard, 0)}
                className="rounded-[8px] h-[45px] w-full text-[14px] font-semibold bg-warningContainer disabled:bg-disable disabled:text-on-disable"
              >
                Uncollected
              </Button>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onClick={onClose} className="!bg-[rgba(0,0,0,0.50)]" />
      </Sheet>
      {isShowChangeCollectCountPopup &&
        createPortal(
          <ChangeCollectedCountPopup
            onClose={() => setIsShowChangeCollectCountPopup(false)}
            onConfirm={updateCollectedCount}
            photoCard={photoCard}
          />,
          document.body
        )}
    </>
  );
};

export default PhotoCardDetailBottomSheet;
